/**
 * Putting the region's placed geometry back on the ground once the ground
 * stopped being flat (`packages/engines flight/terrain-relief.ts`).
 *
 * Everything Red Baron places sits at `z = 0` in its own frame because the
 * world it was authored for had exactly one ground height. With relief under
 * it, that assumption leaves rivers hanging in the air over valleys and
 * farmhouses buried in hillsides, so each placed thing has to be lowered onto
 * the surface. There are three kinds and they want three different answers:
 *
 * 1. **Region-scale flat plates** — the landcover patchwork, the road and river
 *    networks, the field blocks. These are tens of kilometres across and, in
 *    every shipped region, **placed exactly once at rotation 0** — so their
 *    geometry already *is* world geometry, and the honest fix is to drape them
 *    per vertex. A river then runs along the floor of its own valley, which is
 *    the whole point of deriving the valleys from the rivers.
 * 2. **Objects** — buildings, hangars, trenches, tree copses. Small against the
 *    relief's wavelength, placed many times, and instanced. Draping them per
 *    vertex would break the instancing for no visible gain, so each instance is
 *    dropped bodily to the height under its own origin. A 40 m hangar on a 2 km
 *    roll is flat ground as far as the eye is concerned.
 * 3. **Hairlines** — the one-original-pixel edge network
 *    (`polygon-outlines.ts`). Already merged into a single world-space buffer,
 *    so it drapes with one pass — cut on the same cells, because Verdun's
 *    longest segment is 11.4 km and a chord that long is nowhere near the
 *    ground it is meant to lie on.
 *
 * The ten hills are excluded from all three: they carry their own relief and
 * the field is graded flat beneath them, so dropping them would be a no-op that
 * could only introduce error.
 *
 * **Two things here were a user-visible z-fighting bug** (report, 2026-08-23):
 * the sampler the drape uses (see `GroundSampler` below) and what "per vertex"
 * means for a polygon far larger than the surface's own cells (see
 * `splitForDrape` and `three/lattice-clip.ts`).
 */

import * as THREE from "three/webgpu";
import { clipSegmentToLattice, clipTriangleToLattice, type GroundLattice } from "./lattice-clip.ts";
import { GROUND_DATUM_OFFSET_M } from "./relief-ground.ts";

/**
 * The height a thing placed at the datum must be lifted to.
 *
 * Deliberately a bare function rather than a `ReliefField`, because the thing
 * everything must drape onto is the mesh's own piecewise-linear surface and not
 * the analytic field the mesh was built from. See `ReliefGround.sampleAt` for
 * what goes wrong otherwise, and `ReliefGround.drapeAt` for why this is that
 * sampler *plus the datum offset back*.
 */
export type GroundSampler = (x: number, z: number) => number;

/**
 * The merged hairline buffers, structurally — `polygon-outlines.ts`'s
 * `SegmentBuffers`, described here rather than imported so this module stays a
 * leaf of the geometry layer.
 */
export interface DrapedSegments {
  readonly positions: Float32Array;
  readonly colors: Float32Array;
  readonly segmentCount: number;
}

/**
 * Bounding radius past which a single-placement object is draped per vertex
 * rather than dropped bodily. Between the largest building (30 m) and the
 * smallest region-scale plate (a 6 km road spur) by a wide margin.
 */
export const DRAPE_MIN_RADIUS_M = 2000;

/**
 * The relief mesh is drawn `GROUND_DATUM_OFFSET_M` below the datum, and the
 * sampler every drape here uses (`ReliefGround.drapeAt`) adds that offset back —
 * so something placed *at* the datum keeps exactly the 8 cm of clearance it has
 * over today's flat plane. That is the separation `flight/world.ts` measured
 * against a user's z-fighting report, and the reason none of this needs
 * `polygonOffset`.
 *
 * **This was the bug behind the 2026-08-23 "a lot of z-fighting" report.** The
 * drape was handed `sampleAt`, whose heights *already carry* the mesh's
 * sub-datum offset, so `y = 0 + sampleAt(x, z)` put every plate, every road and
 * river hairline and every dropped object exactly **on** the drawn surface with
 * zero clearance — the one arrangement a depth buffer cannot arbitrate. The
 * flat world's 8 cm had been silently spent.
 */

/** True when this ref should be draped per vertex rather than dropped as a whole. */
export function wantsVertexDrape(boundingRadius: number, placementCount: number): boolean {
  return placementCount === 1 && boundingRadius >= DRAPE_MIN_RADIUS_M;
}

/**
 * Splits every triangle of a non-indexed geometry on the ground mesh's own
 * cells, so each output triangle lies inside a single mesh triangle.
 *
 * **Why the mesh's cells and not a fixed edge length.** Draping moves
 * *vertices*, so a polygon is only on the ground where it has one. Verdun's
 * `TERR5-72` is 43 triangles over 42 x 40 km; draping one by its three corners
 * is not a field lying on the ground, it is a rigid 20 km sheet tilted through
 * the hills. The first fix for that was a fixed 100 m subdivision chosen against
 * the mesh's finest spacing (~104 m under the nose) — and it is *still* wrong,
 * because the mesh's spacing is graded and reaches ~1 km at the rim, and because
 * a plate triangle that spans a crease in the mesh chords across it whatever its
 * length. Measured on Verdun's real relief, the bare ground came up through the
 * fields by **0.44 m near the spawn and 1.0 m twenty kilometres out**, mean
 * 29 mm — two surfaces interleaving over the whole map. `lattice-clip.ts` has
 * the full argument and the numbers.
 *
 * Cutting on the mesh's cells and diagonals instead makes coplanarity a
 * property of the construction: measured poke-through **3e-14 m**. It is also
 * cheaper — a 6 km test plate falls from 12 288 triangles to 3 441 at the spawn
 * and 589 at twenty kilometres, because the output tracks the mesh's own
 * resolution instead of a constant.
 *
 * **Every attribute is rebuilt, not just position.** An earlier version resized
 * `position` and `color` and left `normal` and the two `ramp-shade.ts` scalars
 * at their original lengths, which a real GPU rejected outright —
 * *"Vertex range (count: 6642) requires a larger buffer (79704) than the bound
 * buffer size (936)"* — and the region drew nothing. Carrying them generically
 * also means a future attribute cannot reintroduce the same bug.
 *
 * The non-position attributes are copied rather than interpolated, and every
 * piece takes its source face's value. That is exact here: these geometries are
 * non-indexed and flat-shaded (`model-geometry.ts`), so all three corners of a
 * triangle already carry identical normals, colours and ramp fields.
 *
 * `originX`/`originZ` are the placement's own translation: the geometry is in
 * its object frame, and the lattice is world space.
 */
export function splitForDrape(
  geometry: THREE.BufferGeometry,
  lattice: GroundLattice,
  originX: number,
  originZ: number,
): void {
  const position = geometry.getAttribute("position");
  if (!position) return;

  const src = position.array as ArrayLike<number>;
  const carried = Object.entries(geometry.attributes)
    .filter(([name]) => name !== "position")
    .map(([name, attribute]) => ({
      name,
      itemSize: attribute.itemSize,
      src: attribute.array as ArrayLike<number>,
      out: [] as number[],
    }));

  const outPos: number[] = [];
  /** Index of the source triangle being cut, for the carried attributes. */
  let face = 0;

  const keep = (p: Float64Array): void => {
    // Back into the object frame the instance matrix will translate again.
    for (let corner = 0; corner < 3; corner++) {
      outPos.push(p[corner * 3]! - originX, p[corner * 3 + 1]!, p[corner * 3 + 2]! - originZ);
    }
    for (const attribute of carried) {
      const at = face * 3 * attribute.itemSize;
      for (let corner = 0; corner < 3; corner++) {
        for (let k = 0; k < attribute.itemSize; k++) attribute.out.push(attribute.src[at + k] ?? 0);
      }
    }
  };

  const world = new Float64Array(9);
  const faceCount = Math.floor(src.length / 9);
  for (face = 0; face < faceCount; face++) {
    for (let corner = 0; corner < 3; corner++) {
      world[corner * 3] = src[face * 9 + corner * 3]! + originX;
      world[corner * 3 + 1] = src[face * 9 + corner * 3 + 1]!;
      world[corner * 3 + 2] = src[face * 9 + corner * 3 + 2]! + originZ;
    }
    clipTriangleToLattice(world, lattice, keep);
  }

  geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(outPos), 3));
  for (const attribute of carried) {
    geometry.setAttribute(attribute.name, new THREE.BufferAttribute(new Float32Array(attribute.out), attribute.itemSize));
  }
}

/**
 * Lifts every vertex of an already-world-oriented geometry onto the surface,
 * cutting it on the ground mesh's cells first so that "on the surface" is true
 * across the whole polygon and not only at its corners. `originX`/`originZ` are
 * the placement's own translation, because the geometry is still in its object
 * frame.
 *
 * Vertices already above the datum — a hill, a building's roof — are moved by
 * the *same* offset as the ground under them rather than being flattened onto
 * it, so an object keeps its own shape.
 */
export function drapeGeometry(
  geometry: THREE.BufferGeometry,
  sample: GroundSampler,
  lattice: GroundLattice,
  originX: number,
  originZ: number,
): void {
  splitForDrape(geometry, lattice, originX, originZ);
  const position = geometry.getAttribute("position");
  if (!position) return;
  const array = position.array as Float32Array;
  for (let i = 0; i < array.length; i += 3) {
    array[i + 1] = array[i + 1]! + sample(array[i]! + originX, array[i + 2]! + originZ);
  }
  position.needsUpdate = true;
  geometry.computeBoundingSphere();
}

/**
 * Drapes the merged hairline buffer, cutting every segment on the ground mesh's
 * own cells first. Positions are already world space
 * (`polygon-outlines.ts`'s `buildSegmentBuffers`), six floats per segment — two
 * endpoints of three — so this returns **new** buffers rather than mutating in
 * place: a cut segment is more segments than it started as.
 *
 * Cutting matters more here than for the plates. Verdun's outline segments reach
 * 11.4 km, and a straight chord over ±42 m of relief buries a river in every
 * hill and floats it over every valley. See `lattice-clip.ts`.
 */
export function drapeSegments(
  buffers: DrapedSegments,
  sample: GroundSampler,
  lattice: GroundLattice,
): DrapedSegments {
  const positions: number[] = [];
  const colors: number[] = [];
  const source = new Float64Array(6);
  for (let segment = 0; segment < buffers.segmentCount; segment++) {
    const at = segment * 6;
    for (let k = 0; k < 6; k++) source[k] = buffers.positions[at + k]!;
    clipSegmentToLattice(source, lattice, (piece) => {
      for (let end = 0; end < 2; end++) {
        const e = end * 3;
        positions.push(piece[e]!, piece[e + 1]! + sample(piece[e]!, piece[e + 2]!), piece[e + 2]!);
        // Every piece keeps the segment's own two endpoint colours; the
        // original's lines are flat, so both ends carry the same entry and
        // there is nothing to interpolate.
        colors.push(buffers.colors[at + e]!, buffers.colors[at + e + 1]!, buffers.colors[at + e + 2]!);
      }
    });
  }
  return {
    positions: new Float32Array(positions),
    colors: new Float32Array(colors),
    segmentCount: positions.length / 6,
  };
}

/**
 * A second colour array for a draped plate, hillshaded by each piece's own
 * slope — ENHANCED TERRAIN's visible half (`world/landcover-shading.ts`).
 *
 * Only correct **after** `drapeGeometry`, and only because of it: cut on the
 * ground mesh's cells, every piece lies inside one ground triangle, so the
 * piece's own geometric normal is that triangle's normal. Before the cut a
 * plate triangle straddled several and had no single slope to shade by.
 *
 * Returns a new array rather than writing the attribute, so the caller can keep
 * the flat one and make the toggle a buffer swap instead of a rebuild.
 */
export function shadedPlateColours(
  geometry: THREE.BufferGeometry,
  shade: (tone: number, normalX: number, normalY: number, normalZ: number) => number,
): Float32Array | undefined {
  const position = geometry.getAttribute("position");
  const colour = geometry.getAttribute("color");
  if (!position || !colour) return undefined;
  const p = position.array as ArrayLike<number>;
  const source = colour.array as ArrayLike<number>;
  const out = new Float32Array(source.length);
  for (let t = 0; t + 8 < p.length; t += 9) {
    const ax = p[t + 3]! - p[t]!, ay = p[t + 4]! - p[t + 1]!, az = p[t + 5]! - p[t + 2]!;
    const bx = p[t + 6]! - p[t]!, by = p[t + 7]! - p[t + 1]!, bz = p[t + 8]! - p[t + 2]!;
    const tone =
      (Math.round(source[t]! * 255) << 16) | (Math.round(source[t + 1]! * 255) << 8) | Math.round(source[t + 2]! * 255);
    const nx = ay * bz - az * by;
    const ny = az * bx - ax * bz;
    const nz = ax * by - ay * bx;
    // Terrain is drawn `DoubleSide`, so a plate's own winding is not a promise
    // about which way its normal faces — and a downward normal would shade
    // every slope backwards. Ground faces up, so that settles the sign.
    const flip = ny < 0 ? -1 : 1;
    const shaded = shade(tone, flip * nx, flip * ny, flip * nz);
    for (let corner = 0; corner < 3; corner++) {
      out[t + corner * 3] = ((shaded >> 16) & 0xff) / 255;
      out[t + corner * 3 + 1] = ((shaded >> 8) & 0xff) / 255;
      out[t + corner * 3 + 2] = (shaded & 0xff) / 255;
    }
  }
  return out;
}

/** The offset the relief mesh itself is drawn at, re-exported so callers do not have to know which module owns it. */
export const RELIEF_MESH_OFFSET_M = GROUND_DATUM_OFFSET_M;
